import { Box, Grid, makeStyles, TextField, Theme, Typography } from '@freighthub/silhouette-core'
import { FC, useEffect, useRef } from 'react'
import { useController } from 'react-hook-form'
import { useRootStore } from 'service/useRootStore'
import { useI18n } from 'utils/localisation'

import { QuoteRequestFormState } from '../quoteRequestUtils'
import { CustomsIssuance } from './CustomsIssuance'
import { QrSmallButtonFaq } from './QrButtonFaq'
import { QRCheckboxRow } from './QRCheckboxRow'

type StyleProps = {
  showHsCodeCountDigitWarning: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  instructions: {
    margin: theme.spacing(1, 0),
  },
  nested: {
    marginLeft: theme.spacing(5),
  },
  hsCodeWarning: ({ showHsCodeCountDigitWarning }) => ({
    color: showHsCodeCountDigitWarning ? theme.palette.grey['500'] : 'transparent',
    fontStyle: 'italic',
  }),
}))

export type LeanQRCustomsClearanceProps = {
  isOriginEUCountry: boolean
  hsCodesCount?: number
  onHsCodesCountChange: (value: number) => void
  customs?: boolean
  onCustomsChange: (checked: boolean) => void
  customsIssuance?: boolean
  onCustomsIssuanceChange: (checked: boolean) => void
  onCustomsBondTypeChange: (value: string) => void
  onShowHsCodesFaq: () => void
}
export const LeanQRCustomsClearance: FC<LeanQRCustomsClearanceProps> = ({
  isOriginEUCountry,
  hsCodesCount = '',
  onHsCodesCountChange,
  customs = false,
  onCustomsChange,
  customsIssuance = false,
  onCustomsIssuanceChange,
  onCustomsBondTypeChange,
  onShowHsCodesFaq,
}) => {
  const showHsCodeCountDigitWarning = hsCodesCount.toString().length > 3

  const classes = useStyles({ showHsCodeCountDigitWarning })
  const { translate } = useI18n()
  const didMount = useRef(false)

  useEffect(() => {
    if (isOriginEUCountry && !didMount.current) {
      onCustomsChange(true)
      didMount.current = true
    }
  }, [isOriginEUCountry, onCustomsChange])

  const setCustoms = (checked: boolean) => {
    onCustomsChange(checked)
    onCustomsIssuanceChange(checked)
    if (!checked) {
      onCustomsBondTypeChange('')
    }
  }

  return (
    <>
      <QRCheckboxRow
        name="customs"
        checked={customs}
        onCheck={setCustoms}
        label={translate('quote_request-customs_clearance-label')}
        help={
          <div>
            <small>{translate('quote_request-customs_clearance-helper')}</small>
            <br />
            <QrSmallButtonFaq category="service" questions={['customs_clearance']}>
              {translate('quote_request-customs_clearance-more_information')}
            </QrSmallButtonFaq>
          </div>
        }
      >
        <Typography variant="body1" paragraph className={classes.instructions}>
          {translate('quote_request-customs-hs_codes-text')}
          &nbsp;
          <Typography color="primary" role="button" component="a" onClick={onShowHsCodesFaq}>
            {translate('quote_request-customs-hs_codes-help')}
          </Typography>
        </Typography>
        <Grid container alignItems="baseline">
          <Grid>
            <Box my={2} width="320px">
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 1000 }}
                required
                label={translate('quote_request-customs-hs_codes_count-label')}
                name="hs-codes-count"
                value={hsCodesCount}
                onChange={(e) => onHsCodesCountChange(e.target.value)}
              />
            </Box>
            <Typography variant="caption" className={classes.hsCodeWarning} aria-hidden={showHsCodeCountDigitWarning}>
              {translate('quote_request-customs-hs_codes_count-digit_warning')}
            </Typography>
          </Grid>
        </Grid>
        {isOriginEUCountry && (
          <small id="eu-customs-count-warning">{translate('quote_request-customs-hs_codes_count-warning')}</small>
        )}
      </QRCheckboxRow>

      <div className={classes.nested}>
        {isOriginEUCountry && (
          <CustomsIssuance disabled={!customs} checked={customsIssuance} onCheck={onCustomsIssuanceChange} />
        )}
      </div>
    </>
  )
}

type QRCustomsClearanceProps = {
  isOriginEUCountry: boolean
}
export const QRCustomsClearance: FC<QRCustomsClearanceProps> = ({ isOriginEUCountry }) => {
  const { faqService } = useRootStore()
  const { field: hsCodesCountField } = useController<QuoteRequestFormState, 'hsCodesCount'>({ name: 'hsCodesCount' })
  const { field: customsField } = useController<QuoteRequestFormState, 'customs'>({ name: 'customs' })
  const { field: customsIssuanceField } = useController<QuoteRequestFormState, 'customsIssuance'>({
    name: 'customsIssuance',
  })
  const {
    field: { onChange: onCustomsBoudTypeChange },
  } = useController<QuoteRequestFormState, 'customsBondType'>({ name: 'customsBondType' })

  return (
    <LeanQRCustomsClearance
      hsCodesCount={hsCodesCountField.value}
      onHsCodesCountChange={hsCodesCountField.onChange}
      customs={customsField.value}
      onCustomsChange={customsField.onChange}
      customsIssuance={customsIssuanceField.value}
      onCustomsIssuanceChange={customsIssuanceField.onChange}
      onCustomsBondTypeChange={onCustomsBoudTypeChange}
      isOriginEUCountry={isOriginEUCountry}
      onShowHsCodesFaq={() => faqService.show('definitions', ['hscodes'])}
    />
  )
}
